import { cn } from '@/lib/utils'

const STEPS = [
    {
        id: 'selectFormat',
        title: 'Choose Ad Format',
        condition: () => true,
    },
    {
        id: 'leadForm',
        title: 'Attach Instant Form',
        condition: (adCreative) => adCreative.showLeadForm,
        children: [
            {
                id: 'formType',
                title: 'Form Type',
                condition: (adCreative) => adCreative.newLeadForm,
            },
            {
                id: 'formIntro',
                title: 'Intro',
                condition: (adCreative) => adCreative.newLeadForm,
            },
            {
                id: 'formQuestions',
                title: 'Custom Questions',
                condition: (adCreative) => adCreative.newLeadForm,
            },
            {
                id: 'formPrivacy',
                title: 'Privacy Policy',
                condition: (adCreative) => adCreative.newLeadForm,
            },
            {
                id: 'formThankYou',
                title: 'Thank You Screen',
                condition: (adCreative) => adCreative.newLeadForm,
            },
        ],
    },
    {
        id: 'uploadAssets',
        title: 'Upload Creative Assets',
        condition: () => true,
        children: [
            {
                id: 'aiTextToImage',
                title: 'Generate Assets',
                condition: (adCreative) =>
                    adCreative.assetSource === 'aiImage' && adCreative.assetType === 'textToImage',
            },
            {
                id: 'aiImageToImage',
                title: 'Generate Assets',
                condition: (adCreative) =>
                    adCreative.assetSource === 'aiImage' && adCreative.assetType === 'imageToImage',
            },
            {
                id: 'aiAssetLibrary',
                title: 'Generate Assets',
                condition: (adCreative) =>
                    adCreative.assetSource === 'aiImage' && adCreative.assetType === 'aiAssetLibrary',
            },
            {
                id: 'aiTextToVideo',
                title: 'Generate Video',
                condition: (adCreative) =>
                    adCreative.assetSource === 'aiImage' && adCreative.assetType === 'textToVideo',
            },
        ],
    },
    {
        id: 'carouselCards',
        title: 'Carousel Cards',
        condition: (adCreative) => adCreative.format === 'carousel',
    },
    {
        id: 'videoTrim',
        title: 'Trim Video',
        condition: (adCreative) => adCreative.format === 'video',
    },
    {
        id: 'setVideoThumbnail',
        title: 'Set Video Thumbnail',
        condition: (adCreative) => adCreative.format === 'video',
    },
    {
        id: 'setMediaCrop',
        title: 'Set Media Crop For Placement',
        condition: (adCreative) => adCreative.format !== 'carousel',
    },
    {
        id: 'setAdContent',
        title: 'Add Primary Text And Creative Elements',
        condition: () => true,
    },
    {
        id: 'reviewSave',
        title: 'Review & Publish',
        condition: () => true,
    },
]

const CompletedStepIcon = () => {
    return (
        <span className="tw:bg-green-500 tw:rounded-full tw:size-4 tw:min-h-4 tw:min-w-4 tw:flex tw:items-center tw:justify-center tw:mt-0.5">
            <svg width="8" height="7" viewBox="0 0 8 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M1 4.22727L2.10363 5.565C2.50361 6.04983 3.24639 6.04982 3.64637 5.565L7 1.5"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </span>
    )
}

const WizardSteps = ({ currentStep, adCreative }) => {
    const getFlattenedSteps = (steps) => {
        const flattened = []

        const processStep = (step) => {
            flattened.push(step.id)
            if (step.children) step.children.forEach(processStep)
        }

        steps.forEach(processStep)
        return flattened
    }

    const flattenedSteps = getFlattenedSteps(STEPS, adCreative)
    const currentStepIndex = flattenedSteps.indexOf(currentStep)

    const isStepCompleted = (stepId) => {
        const stepIndex = flattenedSteps.indexOf(stepId)
        return stepIndex !== -1 && stepIndex < currentStepIndex
    }

    const renderStep = (step, isChild = false) => {
        // Check if step meets condition
        if (!step.condition(adCreative)) return null

        const isCompleted = isStepCompleted(step.id)
        const isCurrent = currentStep === step.id

        return (
            <div key={step.id}>
                <Step
                    step={step}
                    currentStep={currentStep}
                    isCompleted={isCompleted}
                    isCurrent={isCurrent}
                    isChild={isChild}
                />
                {step.children && step.children.map((childStep) => renderStep(childStep, true))}
            </div>
        )
    }

    return (
        <nav className="tw:min-w-[240px] tw:max-w-[240px] tw:border-r tw:border-border tw:px-3 tw:py-4 tw:h-full">
            {STEPS.map((step) => renderStep(step))}
        </nav>
    )
}

const Step = ({ step, isCompleted, isCurrent, isChild = false }) => {
    return (
        <div
            className={cn('tw:!bg-transparent tw:text-[13px] tw:gap-3 tw:py-1.5 tw:my-1', {
                'tw:outline-[2px] tw:rounded-lg tw:outline-blue-500 tw:!bg-[#6980AA29]': isCurrent,
            })}
        >
            <div
                className={cn('tw:flex tw:items-start tw:gap-3 tw:!pt-0 tw:relative', {
                    'tw:ml-3': !isChild,
                    'tw:ml-6': isChild, // Indent child steps more
                })}
            >
                {!isCompleted && (
                    <span
                        className={cn(
                            'tw:border-2 tw:border-gray-200 tw:rounded-full tw:size-4 tw:min-h-4 tw:min-w-4 tw:mt-0.5',
                        )}
                    ></span>
                )}
                {isCompleted && <CompletedStepIcon />}
                <span>{step.title}</span>
            </div>
        </div>
    )
}

export default WizardSteps
